import * as React from "react";
import { API } from "../API";
import { Loading, loading } from "../Loading";
import { Caregiver, CaregiverCreateParams } from "../messages/Caregiver";
import { CaregiverCertification, Office } from "../messages";
import CaregiverBasicDetailsScreen from "src/components/CaregiverBasicDetailsScreen";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "src/store/store";

interface ExistingCaregiverBasicDetailsActivityProps {
    api: API;
    onSuccessfulLogin: (caregiver: Caregiver, photoUrl: string | null) => void;
    caregiver: Caregiver;
    photoUrl: string | null;
}

const ExistingCaregiverBasicDetailsActivity = (props: ExistingCaregiverBasicDetailsActivityProps) => {
    const [showInternetError, setShowInternetError] = useState<boolean | Loading>(false);
    const activeOffices: Office[] = useSelector((state: AppState) =>
        state.offices.agencyOffices.filter(office => office.active));
    const activeCertifications: CaregiverCertification[] = useSelector((state: AppState) =>
        state.certifications.agencyCertifications);
    const caregiverActiveOffices = activeOffices.filter(office => {
        return office.active && props.caregiver.officeIds.indexOf(office.id) !== -1;
    });
    const caregiverActiveCertifications = props.caregiver.certifications.filter(certification => {
        return activeCertifications.includes(certification);
    });

    const createParams: CaregiverCreateParams = {
        address: props.caregiver.address,
        firstName: props.caregiver.firstName,
        lastName: props.caregiver.lastName,
        middleName: props.caregiver.middleName === null ? undefined : props.caregiver.middleName,
        address2: props.caregiver.address2 === null ? undefined : props.caregiver.address2,
        addressGeoLocation: props.caregiver.addressGeoLocation,
        addressComponents: props.caregiver.addressComponents === null ? props.caregiver.addressComponents : null,
        languages: props.caregiver.languages,
        status: props.caregiver.status,
        birthDateDate: props.caregiver.birthDateDate,
        caregiverCode: props.caregiver.caregiverCode,
        certification: caregiverActiveCertifications[0],
        gender: props.caregiver.gender,
        homePhone: props.caregiver.homePhone,
        phoneNumber: props.caregiver.phoneNumber,
        photoUrl: props.caregiver.photoUrl === null ? props.photoUrl : props.caregiver.photoUrl,
        caregiverBranch: props.caregiver.caregiverBranch,
        email: props.caregiver.email === null ? undefined : props.caregiver.email,
        officeIds: caregiverActiveOffices.length !== 0 ? caregiverActiveOffices.map(office => office.id) : [activeOffices[0].id]
    };

    const handleSubmitClick = async (createParams: CaregiverCreateParams, photoUrl: string | null) => {
        setShowInternetError(loading());
        const didUpdate = await props.api.caregiverUpdateProfile(
            {
                firstName: createParams.firstName,
                middleName: createParams.middleName === null ? undefined : createParams.middleName,
                lastName: createParams.lastName,
                address: createParams.address,
                addressGeoLocation: createParams.addressGeoLocation,
                addressComponents: createParams.addressComponents !== null ? createParams.addressComponents : null,
                languages: createParams.languages,
                birthDateDate: createParams.birthDateDate,
                certification: createParams.certification,
                gender: createParams.gender,
                homePhone: createParams.homePhone,
                email: createParams.email,
                address2: createParams.address2 === null ? undefined : createParams.address2,
                officeIds: createParams.officeIds
            }
        );
        if (didUpdate) {
            setShowInternetError(false);
            const tempCaregiver = { ...props.caregiver };
            if (createParams.addressGeoLocation === undefined || createParams.birthDateDate === undefined) {
                throw new Error("The impossible happened");
            }
            tempCaregiver.firstName = createParams.firstName;
            tempCaregiver.lastName = createParams.lastName;
            tempCaregiver.addressGeoLocation = createParams.addressGeoLocation;
            tempCaregiver.addressComponents = createParams.addressComponents !== null ? createParams.addressComponents : null;
            tempCaregiver.languages = createParams.languages;
            tempCaregiver.birthDateDate = createParams.birthDateDate;
            if (!caregiverActiveCertifications.includes(createParams.certification)) {
                caregiverActiveCertifications.push(createParams.certification);
            }
            tempCaregiver.certifications = caregiverActiveCertifications;
            tempCaregiver.gender = createParams.gender;
            tempCaregiver.homePhone = createParams.homePhone;
            tempCaregiver.officeIds = createParams.officeIds;
            const tempPhotoURL = photoUrl === createParams.photoUrl ? null : photoUrl;

            props.onSuccessfulLogin(tempCaregiver, tempPhotoURL);
        } else {
            setShowInternetError(true);
        }
    };

    return (
        <CaregiverBasicDetailsScreen
            phoneNumber={props.caregiver.phoneNumber}
            onSubmitClick={(createParams, photoUrl) => handleSubmitClick(createParams, photoUrl)}
            showInternetError={showInternetError}
            createParams={createParams}
            title={"Please verify the following details"}
            certificationsInProgress={caregiverActiveCertifications}
        />
    );
};

export default ExistingCaregiverBasicDetailsActivity;



// WEBPACK FOOTER //
// ./src/activities/ExistingCaregiverBasicDetailsActivity.tsx